import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Edit3,
  Loader2,
  Plus,
  RefreshCcw,
  Search,
  ShieldBan,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  AdminPackagingMaterial,
  AdminPackagingMaterialInput,
  AdminPackagingMaterialUpdateInput,
  createPackagingMaterial,
  fetchPackagingMaterials,
  updatePackagingMaterial,
  updatePackagingMaterialActivation,
} from '@/services/adminService';
import { useToast } from '@/hooks/use-toast';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { selectMeasurementUnit, setMeasurementUnit } from '@/features/preferences/preferencesSlice';
import { MEASUREMENT_UNITS, type MeasurementUnit, translateMeasurementUnitLabel } from '@/types/measurement';
import { convertInchesToUnit, convertUnitToInches, unitPrecision } from '@/utils/measurement';
import { cn } from '@/lib/utils';

const MAX_LENGTH = 120;

const formatNumber = (value: number | null | undefined, precision: number) => {
  if (value == null || Number.isNaN(value)) {
    return '';
  }
  return value.toFixed(precision);
};

const filterMaterials = (materials: ReadonlyArray<AdminPackagingMaterial>, query: string) => {
  if (!query.trim()) {
    return materials;
  }
  const normalized = query.trim().toLowerCase();
  return materials.filter((material) => {
    const haystack = [
      material.name,
      material.sku,
      material.type,
      material.fitStrategy,
      material.isActive ? 'active' : 'inactive',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalized);
  });
};

const buildInitialFormState = (
  measurementUnit: MeasurementUnit,
  material?: AdminPackagingMaterial,
): FormState => {
  const precision = unitPrecision(measurementUnit);
  const convert = (value: number | null) =>
    value == null ? '' : formatNumber(convertInchesToUnit(value, measurementUnit), precision);

  return {
    name: material?.name ?? '',
    sku: material?.sku ?? '',
    type: material?.type ?? 'BOX',
    fitStrategy: material?.fitStrategy ?? 'flat',
    isActive: material?.isActive ?? true,
    weightGrams: material?.weightGrams != null ? String(material.weightGrams) : '',
    costUsd: material?.costUsd != null ? String(material.costUsd) : '',
    extraPerItemPackagingCostUsd: material?.extraPerItemPackagingCostUsd != null
      ? String(material.extraPerItemPackagingCostUsd)
      : '',
    oneTimePackagingOverheadCostUsd: material?.oneTimePackagingOverheadCostUsd != null
      ? String(material.oneTimePackagingOverheadCostUsd)
      : '',
    extraPerItemPackagingWeightGrams: material?.extraPerItemPackagingWeightGrams != null
      ? String(material.extraPerItemPackagingWeightGrams)
      : '',
    oneTimePackagingOverheadWeightGrams: material?.oneTimePackagingOverheadWeightGrams != null
      ? String(material.oneTimePackagingOverheadWeightGrams)
      : '',
    innerWidth: convert(material?.innerWidthIn ?? null),
    innerHeight: convert(material?.innerHeightIn ?? null),
    innerDepth: convert(material?.innerDepthIn ?? null),
    baseLength: convert(material?.baseLengthIn ?? null),
    crossSection: convert(material?.crossSectionIn ?? null),
    cutAllowance: convert(material?.cutAllowanceIn ?? null),
    minCutLength: convert(material?.minCutLengthIn ?? null),
  } satisfies FormState;
};

type FormState = {
  name: string;
  sku: string;
  type: 'BOX' | 'TUBE';
  fitStrategy: 'flat' | 'roll';
  isActive: boolean;
  weightGrams: string;
  costUsd: string;
  extraPerItemPackagingCostUsd: string;
  oneTimePackagingOverheadCostUsd: string;
  extraPerItemPackagingWeightGrams: string;
  oneTimePackagingOverheadWeightGrams: string;
  innerWidth: string;
  innerHeight: string;
  innerDepth: string;
  baseLength: string;
  crossSection: string;
  cutAllowance: string;
  minCutLength: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const validateForm = (state: FormState): FormErrors => {
  const next: FormErrors = {};

  const requirePositiveNumber = (value: string, field: keyof FormState, canBeZero = true) => {
    const parsed = Number(value);
    if (!value.trim()) {
      next[field] = 'Required';
      return;
    }
    if (!Number.isFinite(parsed) || parsed < 0 || (!canBeZero && parsed === 0)) {
      next[field] = 'Must be a positive number';
    }
  };

  const optionalPositiveNumber = (value: string, field: keyof FormState) => {
    if (!value.trim()) {
      return;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      next[field] = 'Must be a positive number';
    }
  };

  if (!state.name.trim()) {
    next.name = 'Name is required';
  } else if (state.name.length > MAX_LENGTH) {
    next.name = `Max length ${MAX_LENGTH}`;
  }

  if (!state.sku.trim()) {
    next.sku = 'SKU is required';
  } else if (state.sku.length > MAX_LENGTH) {
    next.sku = `Max length ${MAX_LENGTH}`;
  }

  requirePositiveNumber(state.weightGrams, 'weightGrams');
  requirePositiveNumber(state.costUsd, 'costUsd');
  requirePositiveNumber(state.extraPerItemPackagingCostUsd, 'extraPerItemPackagingCostUsd');
  requirePositiveNumber(state.oneTimePackagingOverheadCostUsd, 'oneTimePackagingOverheadCostUsd');
  requirePositiveNumber(state.extraPerItemPackagingWeightGrams, 'extraPerItemPackagingWeightGrams');
  requirePositiveNumber(state.oneTimePackagingOverheadWeightGrams, 'oneTimePackagingOverheadWeightGrams');

  if (state.type === 'BOX') {
    optionalPositiveNumber(state.innerWidth, 'innerWidth');
    optionalPositiveNumber(state.innerHeight, 'innerHeight');
  }

  if (state.type === 'TUBE') {
    optionalPositiveNumber(state.baseLength, 'baseLength');
    optionalPositiveNumber(state.cutAllowance, 'cutAllowance');
    optionalPositiveNumber(state.minCutLength, 'minCutLength');
  }

  return next;
};

const buildPayload = (
  state: FormState,
  measurementUnit: MeasurementUnit,
  existing?: AdminPackagingMaterial,
): AdminPackagingMaterialInput | AdminPackagingMaterialUpdateInput => {
  const parseFloatSafe = (value: string) => Number(value.trim());
  const convertBack = (value: string) => {
    if (!value.trim()) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return convertUnitToInches(parsed, measurementUnit);
  };

  const basePayload: AdminPackagingMaterialInput = {
    name: state.name.trim(),
    sku: state.sku.trim(),
    type: state.type,
    fitStrategy: state.fitStrategy,
    isActive: state.isActive,
    weightGrams: parseFloatSafe(state.weightGrams),
    costUsd: parseFloatSafe(state.costUsd),
    extraPerItemPackagingCostUsd: parseFloatSafe(state.extraPerItemPackagingCostUsd),
    oneTimePackagingOverheadCostUsd: parseFloatSafe(state.oneTimePackagingOverheadCostUsd),
    extraPerItemPackagingWeightGrams: parseFloatSafe(state.extraPerItemPackagingWeightGrams),
    oneTimePackagingOverheadWeightGrams: parseFloatSafe(state.oneTimePackagingOverheadWeightGrams),
    innerWidthIn: convertBack(state.innerWidth),
    innerHeightIn: convertBack(state.innerHeight),
    innerDepthIn: convertBack(state.innerDepth),
    baseLengthIn: convertBack(state.baseLength),
    crossSectionIn: convertBack(state.crossSection),
    cutAllowanceIn: convertBack(state.cutAllowance),
    minCutLengthIn: convertBack(state.minCutLength),
  } satisfies AdminPackagingMaterialInput;

  if (!existing) {
    return basePayload;
  }

  const diffEntries: Array<[
    keyof AdminPackagingMaterialInput,
    AdminPackagingMaterialInput[keyof AdminPackagingMaterialInput],
  ]> = [];

  (Object.keys(basePayload) as Array<keyof AdminPackagingMaterialInput>).forEach((key) => {
    const nextValue = basePayload[key];
    const currentValue = existing[key as keyof AdminPackagingMaterial];
    if (nextValue !== currentValue) {
      diffEntries.push([key, nextValue]);
    }
  });

  return Object.fromEntries(diffEntries) as AdminPackagingMaterialUpdateInput;
};

const AdminPackagingMaterialsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const [materials, setMaterials] = useState<ReadonlyArray<AdminPackagingMaterial>>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<AdminPackagingMaterial | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [formState, setFormState] = useState<FormState>(() => buildInitialFormState(measurementUnit));
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [activationPending, setActivationPending] = useState<Set<string>>(new Set());

  const filteredMaterials = useMemo(
    () => filterMaterials(materials, search),
    [materials, search],
  );

  const loadMaterials = useCallback(async () => {
    try {
      setStatus('loading');
      setError(null);
      const data = await fetchPackagingMaterials();
      setMaterials(data);
      setStatus('idle');
    } catch (err) {
      logging.error('Failed to fetch packaging materials', { error: err });
      const message = 'Failed to load materials.';
      setError(message);
      setStatus('error');
      toast({
        title: 'Failed to load materials',
        description: message,
        variant: 'destructive',
      });
    }
  }, [toast]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    void loadMaterials();
  }, [isAdmin, loadMaterials, navigate]);

  const precision = unitPrecision(measurementUnit);
  const unitLabel = translateMeasurementUnitLabel(t, measurementUnit);

  const openCreateForm = () => {
    analytics.track('button: ui.admin_packaging_material_create_open');
    setEditingMaterial(null);
    setFormVisible(true);
    setFormState(buildInitialFormState(measurementUnit));
    setFormErrors({});
  };

  const openEditForm = (material: AdminPackagingMaterial) => {
    analytics.track('button: ui.admin_packaging_material_edit_open', { id: material.id });
    setEditingMaterial(material);
    setFormVisible(true);
    setFormState(buildInitialFormState(measurementUnit, material));
    setFormErrors({});
  };

  const closeForm = () => {
    setFormVisible(false);
    setEditingMaterial(null);
    setFormState(buildInitialFormState(measurementUnit));
    setFormErrors({});
  };

  const handleChange = <Field extends keyof FormState>(field: Field, value: FormState[Field]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleMeasurementUnitChange = (unit: MeasurementUnit) => {
    dispatch(setMeasurementUnit(unit));
    analytics.track('dropdown: ui.admin_packaging_unit_change', { unit });
    setFormState(buildInitialFormState(unit, editingMaterial ?? undefined));
  };

  const handleSubmit = async () => {
    const validation = validateForm(formState);
    setFormErrors(validation);
    if (Object.keys(validation).length > 0) {
      analytics.track('form: ui.admin_packaging_material_validation_failed', { fieldCount: Object.keys(validation).length });
      return;
    }

    const payload = buildPayload(formState, measurementUnit, editingMaterial ?? undefined);

    try {
      setIsSaving(true);
      if (editingMaterial) {
        const diffKeys = Object.keys(payload);
        if (diffKeys.length === 0) {
          toast({ title: 'No changes to save', description: 'Update fields before saving.' });
          return;
        }
        analytics.track('form: ui.admin_packaging_material_update_submit', { id: editingMaterial.id, changed: diffKeys });
        await updatePackagingMaterial(editingMaterial.id, payload);
        toast({ title: 'Material updated', description: `${formState.name} saved.` });
      } else {
        analytics.track('form: ui.admin_packaging_material_create_submit');
        await createPackagingMaterial(payload as AdminPackagingMaterialInput);
        toast({ title: 'Material created', description: `${formState.name} added.` });
      }
      await loadMaterials();
      closeForm();
    } catch (err) {
      logging.error('Failed to save packaging material', { error: err });
      toast({
        title: 'Failed to save material',
        description: err instanceof Error ? err.message : 'Unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (material: AdminPackagingMaterial, isActive: boolean) => {
    analytics.track('toggle: ui.admin_packaging_material_activation', { id: material.id, isActive });
    setActivationPending((prev) => {
      const next = new Set(prev);
      next.add(material.id);
      return next;
    });
    const previousMaterials = materials;
    try {
      const updated = await updatePackagingMaterialActivation(material.id, isActive);
      setMaterials((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
      toast({
        title: isActive ? 'Material activated' : 'Material deactivated',
        description: material.name,
      });
    } catch (err) {
      logging.error('Failed to toggle material activation', { error: err });
      setMaterials(previousMaterials);
      toast({
        title: 'Failed to update activation',
        description: err instanceof Error ? err.message : 'Unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setActivationPending((prev) => {
        const next = new Set(prev);
        next.delete(material.id);
        return next;
      });
    }
  };

  const renderDimensionColumn = (valueInches: number | null): string => {
    if (valueInches == null) {
      return '—';
    }
    return formatNumber(convertInchesToUnit(valueInches, measurementUnit), precision);
  };

  useEffect(() => {
    if (!formVisible) {
      return;
    }
    setFormState(buildInitialFormState(measurementUnit, editingMaterial ?? undefined));
  }, [formVisible, measurementUnit, editingMaterial]);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Packaging materials</h1>
          <p className="text-muted-foreground">
            Manage box and tube options used for automatic packaging weight and cost calculations.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={measurementUnit} onValueChange={(value) => handleMeasurementUnitChange(value as MeasurementUnit)}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Unit" />
            </SelectTrigger>
            <SelectContent>
              {MEASUREMENT_UNITS.map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {translateMeasurementUnitLabel(t, unit)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => {
              analytics.track('button: ui.admin_packaging_material_reload');
              void loadMaterials();
            }}
            disabled={status === 'loading'}
          >
            {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
            Reload
          </Button>
          <Button onClick={openCreateForm} disabled={formVisible && !editingMaterial}>
            <Plus className="mr-2 h-4 w-4" />
            New material
          </Button>
        </div>
      </div>

      {formVisible ? (
        <Card className="mb-8">
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>{editingMaterial ? 'Edit packaging material' : 'Create packaging material'}</CardTitle>
              <CardDescription>
                {editingMaterial ? 'Update dimensions, costs, and availability.' : 'Define a new packaging option for orders.'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeForm} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSaving}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                Save material
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formState.name}
                  onChange={(event) => handleChange('name', event.target.value)}
                  placeholder="Archival Box 18x24"
                  maxLength={MAX_LENGTH}
                />
                {formErrors.name ? <p className="text-xs text-destructive">{formErrors.name}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formState.sku}
                  onChange={(event) => handleChange('sku', event.target.value)}
                  placeholder="BX-1824-COTTON"
                  maxLength={MAX_LENGTH}
                />
                {formErrors.sku ? <p className="text-xs text-destructive">{formErrors.sku}</p> : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formState.type}
                  onValueChange={(value) => {
                    const nextType = value as 'BOX' | 'TUBE';
                    handleChange('type', nextType);
                    handleChange('fitStrategy', nextType === 'BOX' ? 'flat' : 'roll');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BOX">Box</SelectItem>
                    <SelectItem value="TUBE">Tube</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fit strategy</Label>
                <Input value={formState.fitStrategy === 'flat' ? 'Flat (auto)' : 'Roll (auto)'} disabled />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="weightGrams">Base weight (g)</Label>
                <Input
                  id="weightGrams"
                  value={formState.weightGrams}
                  onChange={(event) => handleChange('weightGrams', event.target.value)}
                  placeholder="120"
                />
                {formErrors.weightGrams ? <p className="text-xs text-destructive">{formErrors.weightGrams}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="costUsd">Base cost (USD)</Label>
                <Input
                  id="costUsd"
                  value={formState.costUsd}
                  onChange={(event) => handleChange('costUsd', event.target.value)}
                  placeholder="2.95"
                />
                {formErrors.costUsd ? <p className="text-xs text-destructive">{formErrors.costUsd}</p> : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="extraPerItemPackagingCostUsd">Extra per item cost (USD)</Label>
                <Input
                  id="extraPerItemPackagingCostUsd"
                  value={formState.extraPerItemPackagingCostUsd}
                  onChange={(event) => handleChange('extraPerItemPackagingCostUsd', event.target.value)}
                  placeholder="0.25"
                />
                {formErrors.extraPerItemPackagingCostUsd ? (
                  <p className="text-xs text-destructive">{formErrors.extraPerItemPackagingCostUsd}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="oneTimePackagingOverheadCostUsd">One-time overhead cost (USD)</Label>
                <Input
                  id="oneTimePackagingOverheadCostUsd"
                  value={formState.oneTimePackagingOverheadCostUsd}
                  onChange={(event) => handleChange('oneTimePackagingOverheadCostUsd', event.target.value)}
                  placeholder="1.50"
                />
                {formErrors.oneTimePackagingOverheadCostUsd ? (
                  <p className="text-xs text-destructive">{formErrors.oneTimePackagingOverheadCostUsd}</p>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="extraPerItemPackagingWeightGrams">Extra per item weight (g)</Label>
                <Input
                  id="extraPerItemPackagingWeightGrams"
                  value={formState.extraPerItemPackagingWeightGrams}
                  onChange={(event) => handleChange('extraPerItemPackagingWeightGrams', event.target.value)}
                  placeholder="5"
                />
                {formErrors.extraPerItemPackagingWeightGrams ? (
                  <p className="text-xs text-destructive">{formErrors.extraPerItemPackagingWeightGrams}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="oneTimePackagingOverheadWeightGrams">One-time overhead weight (g)</Label>
                <Input
                  id="oneTimePackagingOverheadWeightGrams"
                  value={formState.oneTimePackagingOverheadWeightGrams}
                  onChange={(event) => handleChange('oneTimePackagingOverheadWeightGrams', event.target.value)}
                  placeholder="15"
                />
                {formErrors.oneTimePackagingOverheadWeightGrams ? (
                  <p className="text-xs text-destructive">{formErrors.oneTimePackagingOverheadWeightGrams}</p>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="innerWidth">Inner width ({unitLabel})</Label>
                <Input
                  id="innerWidth"
                  value={formState.innerWidth}
                  onChange={(event) => handleChange('innerWidth', event.target.value)}
                  placeholder={`14${unitLabel}`}
                  disabled={formState.type !== 'BOX'}
                />
                {formErrors.innerWidth ? <p className="text-xs text-destructive">{formErrors.innerWidth}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="innerHeight">Inner height ({unitLabel})</Label>
                <Input
                  id="innerHeight"
                  value={formState.innerHeight}
                  onChange={(event) => handleChange('innerHeight', event.target.value)}
                  placeholder={`12${unitLabel}`}
                  disabled={formState.type !== 'BOX'}
                />
                {formErrors.innerHeight ? <p className="text-xs text-destructive">{formErrors.innerHeight}</p> : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="innerDepth">Inner depth ({unitLabel})</Label>
                <Input
                  id="innerDepth"
                  value={formState.innerDepth}
                  onChange={(event) => handleChange('innerDepth', event.target.value)}
                  placeholder={`2${unitLabel}`}
                  disabled={formState.type !== 'BOX'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="baseLength">Base length ({unitLabel})</Label>
                <Input
                  id="baseLength"
                  value={formState.baseLength}
                  onChange={(event) => handleChange('baseLength', event.target.value)}
                  placeholder={`40${unitLabel}`}
                  disabled={formState.type !== 'TUBE'}
                />
                {formErrors.baseLength ? <p className="text-xs text-destructive">{formErrors.baseLength}</p> : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="crossSection">Cross-section ({unitLabel})</Label>
                <Input
                  id="crossSection"
                  value={formState.crossSection}
                  onChange={(event) => handleChange('crossSection', event.target.value)}
                  placeholder={`4${unitLabel}`}
                  disabled={formState.type !== 'TUBE'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cutAllowance">Cut allowance ({unitLabel})</Label>
                <Input
                  id="cutAllowance"
                  value={formState.cutAllowance}
                  onChange={(event) => handleChange('cutAllowance', event.target.value)}
                  placeholder={`2${unitLabel}`}
                  disabled={formState.type !== 'TUBE'}
                />
                {formErrors.cutAllowance ? <p className="text-xs text-destructive">{formErrors.cutAllowance}</p> : null}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="minCutLength">Minimum cut length ({unitLabel})</Label>
                <Input
                  id="minCutLength"
                  value={formState.minCutLength}
                  onChange={(event) => handleChange('minCutLength', event.target.value)}
                  placeholder={`6${unitLabel}`}
                  disabled={formState.type !== 'TUBE'}
                />
                {formErrors.minCutLength ? <p className="text-xs text-destructive">{formErrors.minCutLength}</p> : null}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="isActive">Active</Label>
                  <p className="text-xs text-muted-foreground">Visible to the packaging selector</p>
                </div>
                <Switch
                  id="isActive"
                  checked={formState.isActive}
                  onCheckedChange={(checked: boolean) => handleChange('isActive', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-2xl">Packaging catalog</CardTitle>
            <CardDescription>Search, filter, and publish packaging materials.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9 pr-10"
                placeholder="Search by name, SKU, type"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {search ? (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status === 'loading' && materials.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading materials…
            </div>
          ) : error && materials.length === 0 ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-destructive">
              {error}
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="rounded-lg border border-dashed border-muted-foreground/40 p-10 text-center text-muted-foreground">
              No packaging materials found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Weight (g)</TableHead>
                    <TableHead>Cost (USD)</TableHead>
                    <TableHead>Extras</TableHead>
                    <TableHead>Dimensions ({unitLabel})</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMaterials.map((material) => {
                    const extrasLabel = `+$${material.extraPerItemPackagingCostUsd.toFixed(2)} / item · $${material.oneTimePackagingOverheadCostUsd.toFixed(2)} one-time`;
                    const dimsLabel =
                      material.type === 'BOX'
                        ? `${renderDimensionColumn(material.innerWidthIn)} × ${renderDimensionColumn(material.innerHeightIn)} × ${renderDimensionColumn(material.innerDepthIn)}`
                        : `${renderDimensionColumn(material.baseLengthIn)} length · ${renderDimensionColumn(material.crossSectionIn)} cross · +${renderDimensionColumn(material.cutAllowanceIn)} allowance`;
                    return (
                      <TableRow
                        key={material.id}
                        className={cn('align-top transition-colors duration-200', !material.isActive && 'bg-amber-50 dark:bg-amber-500/10')}
                      >
                        <TableCell>
                          <div className="font-semibold">{material.name}</div>
                          <div className="text-xs text-muted-foreground">Created {new Date(material.createdAt).toLocaleString()}</div>
                        </TableCell>
                        <TableCell>{material.sku}</TableCell>
                        <TableCell className="space-y-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-medium">
                            {material.type === 'BOX' ? 'Box' : 'Tube'}
                          </span>
                          <div className="text-xs text-muted-foreground capitalize">{material.fitStrategy}</div>
                        </TableCell>
                        <TableCell>{material.weightGrams.toFixed(0)} g</TableCell>
                        <TableCell>${material.costUsd.toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="text-sm">{extrasLabel}</div>
                          <div className="text-xs text-muted-foreground">
                            +{material.extraPerItemPackagingWeightGrams.toFixed(0)} g / item · +{material.oneTimePackagingOverheadWeightGrams.toFixed(0)} g one-time
                          </div>
                        </TableCell>
                        <TableCell>{dimsLabel}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={material.isActive}
                              onCheckedChange={(checked: boolean) => handleToggleActive(material, checked)}
                              aria-label={material.isActive ? `Deactivate ${material.name}` : `Activate ${material.name}`}
                              disabled={activationPending.has(material.id)}
                            />
                            {material.isActive ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                <ShieldCheck className="h-3 w-3" /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <ShieldBan className="h-3 w-3" /> Inactive
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditForm(material)}
                            className="inline-flex items-center gap-2"
                          >
                            <Edit3 className="h-4 w-4" /> Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPackagingMaterialsPage;
